// The admin portal's section nav, shared by every admin screen.
//
// Real routes rather than tabs on one page, matching the rest of the app: the
// History API is the navigation model here, so /admin/groups is a URL an admin
// can bookmark, reload, and send to the other admin.

import { el } from '../dom.js';

export type AdminSection = 'codes' | 'groups' | 'users';

const SECTIONS: Array<{ key: AdminSection; href: string; label: string }> = [
  { key: 'codes', href: '/admin', label: 'Invite codes' },
  { key: 'groups', href: '/admin/groups', label: 'Groups' },
  { key: 'users', href: '/admin/users', label: 'Users' },
];

export function adminSubnav(active: AdminSection): HTMLElement {
  return el(
    'nav',
    { class: 'subnav' },
    SECTIONS.map((s) =>
      // aria-current, not just a class: the active section has to be announced,
      // not only coloured.
      el(
        'a',
        s.key === active ? { href: s.href, class: 'active', 'aria-current': 'page' } : { href: s.href },
        s.label,
      ),
    ),
  );
}

/**
 * The shared failure rendering for every admin screen.
 *
 * The nav hides these pages for a non-admin, but hiding a link is not access
 * control — a typed URL, a stale tab, or an account demoted mid-session all land
 * here legitimately, and 403 has to read as an explanation rather than a bug.
 */
export function adminError(status: number | undefined): HTMLElement[] {
  return [
    el(
      'p',
      { class: 'form-error' },
      status === 403 ? 'You are not a site administrator.' : 'Could not load the admin portal.',
    ),
    el('p', {}, [el('a', { href: '/' }, 'Back to home')]),
  ];
}

/**
 * Row action controls, separated so two of them never render as one word.
 *
 * The admin tables put every action for a row in a single cell, and `el()`
 * appends children with no whitespace between them — so Masquerade beside
 * Suspend came out as `MasqueradeSuspend`, the destructive control pressed
 * directly against the safe one with no gap to aim at. A mis-aimed click there
 * signs an account out of every device and refuses further logins.
 *
 * Found on production 2026-09-09 while driving the Users table.
 *
 * The separator is `aria-hidden`: it is a visual gap, and a screen reader
 * announcing "pipe" between two buttons is noise. Shared rather than copied
 * into each table, because a fix that lands on one of the two is the same bug
 * still shipping.
 */
export function rowActions(buttons: HTMLElement[]): HTMLElement[] {
  return buttons.flatMap((btn, i) =>
    i === 0 ? [btn] : [el('span', { class: 'action-sep', 'aria-hidden': 'true' }, '|'), btn],
  );
}
